import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Client from '../Client';
import Config from '../ClientConfig';

const INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED = 1001;
const INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED = 1002;
const INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY = 1003;

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
function getLocalDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return "";
}

class IntelligenceInsights extends React.Component {
  constructor(props) {
    super(props);

    const defaultDate = getLocalDateString(addDays(new Date(), -1));

    this.state = {
      client: new Client("none"),
      insights: [],
      page: 0,
      rowsPerPage: 5,

      dateFrom: defaultDate,
      dateTo: defaultDate,
      status: "",
      severity: "",
      insightType: "",
      statusView: "open",

      selectedInsight: null,
      reviewNote: "",
      drawerOpen: false,

      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      showMobileFilters: false,
      filterData: {}
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.applyDatePreset = this.applyDatePreset.bind(this);
    this.openInsight = this.openInsight.bind(this);
    this.closeInsightDrawer = this.closeInsightDrawer.bind(this);
    this.handleReviewNoteChange = this.handleReviewNoteChange.bind(this);
    this.setInsightStatus = this.setInsightStatus.bind(this);
    this.closeSnackbar = this.closeSnackbar.bind(this);
    this.handleStatusViewChange = this.handleStatusViewChange.bind(this);
    this.listRef = React.createRef();
  }

  componentDidMount() {
    this.getData();
  }

  getData() {
    let queryData = {};

    if (this.state.dateFrom) {
      queryData.dateFrom = this.state.dateFrom;
    }
    if (this.state.dateTo) {
      queryData.dateTo = this.state.dateTo;
    }
    if (this.state.status !== "") {
      queryData.status = this.state.status;
    }
    if (this.state.severity !== "") {
      queryData.severity = this.state.severity;
    }
    if (this.state.insightType !== "") {
      queryData.insightType = this.state.insightType;
    }

    let data = this.state.client.getData(
      Config.API_URL_INTELLIGENCE_INSIGHTS,
      queryData
    );

    this.setState({
      insights: Array.isArray(data) ? data : [],
      filterData: queryData
    });
  }

  doSync() {
    this.getData();
  }

  handleFilterChange(event) {
    const { name, value } = event.target;
    const nextState = {
      [name]: value,
      page: 0
    };

    // When the user selects From, inspect that day first.
    if (name === "dateFrom") {
      nextState.dateTo = value;
    }
    // Keep the range valid if To is before From.
    if (name === "dateTo" && this.state.dateFrom && value < this.state.dateFrom) {
      nextState.dateFrom = value;
    }

    this.setState(
      nextState,
      () => this.getData()
    );
  }

  clearFilters() {
    const defaultDate = getLocalDateString(addDays(new Date(), -1));

    this.setState(
      {
        dateFrom: defaultDate,
        dateTo: defaultDate,
        status: "",
        severity: "",
        insightType: "",
        statusView: "open",
        page: 0
      },
      () => this.getData()
    );
  }

  handleStatusViewChange(statusView) {
    this.setState({
      statusView,
      status: "",
      page: 0
    });
  }

  applyDatePreset(preset) {
    const today = new Date();

    let dateFrom = getLocalDateString(addDays(today, -1));
    let dateTo = getLocalDateString(addDays(today, -1));

    if (preset === "today") {
      dateFrom = getLocalDateString(today);
      dateTo = getLocalDateString(today);
    }

    if (preset === "last7") {
      dateFrom = getLocalDateString(addDays(today, -7));
      dateTo = getLocalDateString(addDays(today, -1));
    }

    this.setState(
      {
        dateFrom,
        dateTo,
        page: 0
      },
      () => this.getData()
    );
  }

  pushApiData(url, queryData) {
    const client = this.state.client;

    if (!client || typeof client.pushData !== "function") {
      return {
        status: "ERROR",
        message: "Client pushData is not available."
      };
    }

    const originalPrefix = client.prefix;

    try {
      // For this page only:
      // force pushData to use the API URL exactly as provided.
      client.prefix = "";

      return client.pushData(url, queryData);
    } finally {
      client.prefix = originalPrefix;
    }
  }

  getSetStatusUrl(row) {
    const baseUrl = String(Config.API_URL_INTELLIGENCE_INSIGHTS || "").split("?")[0];

    const cleanBaseUrl = baseUrl.endsWith("/")
      ? baseUrl.slice(0, -1)
      : baseUrl;

    return `${cleanBaseUrl}/${row.iref}/set-status/`;
  }

  openInsight(row) {
    this.setState({
      selectedInsight: row,
      reviewNote: row.review_note || "",
      drawerOpen: true
    });
  }

  closeInsightDrawer() {
    this.setState({
      drawerOpen: false,
      selectedInsight: null,
      reviewNote: ""
    });
  }

  handleReviewNoteChange(event) {
    this.setState({
      reviewNote: event.target.value
    });
  }

  closeSnackbar() {
    this.setState({
      snackbarOpen: false
    });
  }

  setInsightStatus(statusValue) {
    const selectedInsight = this.state.selectedInsight;

    if (!selectedInsight) {
      return;
    }

    const payload = {
      status: statusValue,
      review_note: this.state.reviewNote || ""
    };
    const statusUrl = this.getSetStatusUrl(selectedInsight);
    const updatedInsight = this.pushApiData(
      statusUrl,
      payload
    );

    if (
      updatedInsight &&
      (
        updatedInsight.status === "ERROR" ||
        updatedInsight.detail ||
        updatedInsight.message
      )
    ) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: String(
          updatedInsight.detail || updatedInsight.message || "Could not update insight status."
        ).substring(0, 180),
        snackbarSeverity: "error"
      });

      return;
    }

    if (!updatedInsight || !updatedInsight.iref) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Status update did not return a valid insight.",
        snackbarSeverity: "error"
      });

      return;
    }

    const updatedInsights = (this.state.insights || []).map((row) => {
      if (row.iref === updatedInsight.iref) {
        return updatedInsight;
      }

      return row;
    });

    this.setState({
      insights: updatedInsights,
      selectedInsight: updatedInsight,
      reviewNote: updatedInsight.review_note || "",
      snackbarOpen: true,
      snackbarMessage: "Insight status updated.",
      snackbarSeverity: "success"
    });
  }

  handleChangePage = (event, newPage) => {
    this.setState(
      { page: newPage },
      () => this.scrollListToTop()
    );
  }

  scrollListToTop() {
    if (this.listRef && this.listRef.current) {
      this.listRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0
    });
  }

  isDailySummary(row) {
    return Number(row && row.insight_type) === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY;
  }

  getLocationLabel(row) {
    if (this.isDailySummary(row)) {
      return 'All locations';
    }
    return row.location_name || '-';
  }

  getInsightTypeLabel(row) {
    const insightType = Number(row && row.insight_type);

    if (insightType === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY) {
      return 'Daily summary';
    }
    if (insightType === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED) {
      return 'Above expected';
    }
    if (insightType === INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED) {
      return 'Below expected';
    }

    return 'Insight';
  }

  getSeverityColor(severity) {
    if (Number(severity) === 4) {
      return {
        color: '#7f1d1d',
        bgcolor: 'rgba(127, 29, 29, 0.12)',
        border: '1px solid rgba(127, 29, 29, 0.34)'
      };
    }

    if (Number(severity) === 3) {
      return {
        color: '#991b1b',
        bgcolor: 'rgba(239, 68, 68, 0.10)',
        border: '1px solid rgba(239, 68, 68, 0.22)'
      };
    }

    if (Number(severity) === 2) {
      return {
        color: '#92400e',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        border: '1px solid rgba(245, 158, 11, 0.26)'
      };
    }

    return {
      color: '#334155',
      bgcolor: 'rgba(100, 116, 139, 0.10)',
      border: '1px solid rgba(100, 116, 139, 0.20)'
    };
  }

  getStatusColor(status) {
    if (Number(status) === 0) {
      return {
        color: '#1a237e',
        bgcolor: 'rgba(26, 35, 126, 0.08)',
        border: '1px solid rgba(26, 35, 126, 0.14)'
      };
    }

    if (Number(status) === 2) {
      return {
        color: '#075985',
        bgcolor: 'rgba(14, 165, 233, 0.10)',
        border: '1px solid rgba(14, 165, 233, 0.20)'
      };
    }

    if (Number(status) === 3) {
      return {
        color: '#166534',
        bgcolor: 'rgba(34, 197, 94, 0.10)',
        border: '1px solid rgba(34, 197, 94, 0.22)'
      };
    }

    if (Number(status) === 4) {
      return {
        color: '#475569',
        bgcolor: 'rgba(100, 116, 139, 0.10)',
        border: '1px solid rgba(100, 116, 139, 0.18)'
      };
    }

    return {
      color: '#334155',
      bgcolor: 'rgba(100, 116, 139, 0.08)',
      border: '1px solid rgba(100, 116, 139, 0.16)'
    };
  }

  getDirection(row) {
    const insightType = Number(row.insight_type);
    const diff = Number(row.difference_percent || 0);

    if (insightType === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY) {
      return {
        label: 'Summary',
        color: '#1a237e',
        bg: 'rgba(26, 35, 126, 0.08)',
        border: '1px solid rgba(26, 35, 126, 0.16)'
      };
    }
    if (insightType === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED || diff > 0) {
      return {
        label: 'Opportunity',
        color: '#166534',
        bg: 'rgba(34, 197, 94, 0.10)',
        border: '1px solid rgba(34, 197, 94, 0.22)'
      };
    }

    return {
      label: 'Attention',
      color: '#991b1b',
      bg: 'rgba(239, 68, 68, 0.10)',
      border: '1px solid rgba(239, 68, 68, 0.22)'
    };
  }

  getStatusLabel(row) {
    if (row.status_label) {
      return row.status_label;
    }

    const statusMap = {
      0: 'New',
      1: 'Seen',
      2: 'In Review',
      3: 'Resolved',
      4: 'Ignored',
      9: 'Archived'
    };

    return statusMap[Number(row.status)] || '-';
  }

  getSeverityLabel(row) {
    if (row.severity_label) {
      return row.severity_label;
    }

    const severityMap = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Critical'
    };

    return severityMap[Number(row.severity)] || 'Severity';
  }

  formatDate(value) {
    if (!value) {
      return "-";
    }

    return new Date(value).toLocaleDateString("ro-RO", {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  }

  formatMoney(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return numberValue.toLocaleString("ro-RO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  formatMoneyDetailed(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return numberValue.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatPercent(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return `${numberValue > 0 ? '+' : ''}${numberValue.toFixed(1)}%`;
  }

  cleanText(value) {
    if (!value) {
      return "";
    }

    return String(value).replace(/(-?\d+\.\d{2,})(%?)/g, (match, number, percent) => {
      const parsed = Number(number);

      if (!Number.isFinite(parsed)) {
        return match;
      }

      if (percent) {
        return `${parsed.toFixed(1)}%`;
      }

      if (Math.abs(parsed) >= 100) {
        return parsed.toLocaleString("ro-RO", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }

      return parsed.toFixed(1);
    });
  }

  renderInsightCard(row) {
    const direction = this.getDirection(row);
    const severitySx = this.getSeverityColor(row.severity);
    const statusSx = this.getStatusColor(row.status);
    const diffValue = Number(row.difference_percent || 0);
    const isNegative = diffValue < 0;
    const isSummary = this.isDailySummary(row);
    const locationLabel = this.getLocationLabel(row);

    const title = row.title || 'Insight';

    return (
      <Paper
        key={row.iref}
        variant="outlined"
        onClick={() => this.openInsight(row)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            this.openInsight(row);
          }
        }}
        sx={{
          flexShrink: 0,
          borderRadius: { xs: '12px', md: '14px' },
          overflow: 'hidden',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.045)',
          cursor: 'pointer',
          transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '&:hover': {
            borderColor: 'rgba(26, 35, 126, 0.22)',
            boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            minHeight: 112,
            gap: 0,

            '@media (min-width: 1020px)': {
              gridTemplateColumns: 'minmax(280px, 360px) minmax(110px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr)',
              minHeight: 104,
            },
          }}
        >
          <Box
            sx={{
              p: 0.85,
              '@media (min-width: 1020px)': {
                p: 1.25,
              },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                mb: { xs: 0.6, sm: 0.35 },
                flexWrap: 'wrap',
                gap: 0.35,
              }}
            >
              <Chip
                label={direction.label}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                  color: direction.color,
                  bgcolor: direction.bg,
                  border: direction.border,
                }}
              />

              {isSummary ? (
                <Chip
                  label={this.getInsightTypeLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: '#1a237e',
                    bgcolor: 'rgba(26, 35, 126, 0.06)',
                    border: '1px solid rgba(26, 35, 126, 0.14)',
                  }}
                />
              ) : null}

              <Chip
                label={this.getSeverityLabel(row)}
                size="small"
                variant="outlined"
                sx={{
                  ...severitySx,
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                }}
              />

              <Chip
                label={this.getStatusLabel(row)}
                size="small"
                variant="outlined"
                sx={{
                  ...statusSx,
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                }}
              />

              {row.review_note ? (
                <Chip
                  icon={<NotesOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                  label="Note"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: '#334155',
                    bgcolor: 'rgba(100, 116, 139, 0.08)',
                    border: '1px solid rgba(100, 116, 139, 0.16)',
                    '& .MuiChip-icon': {
                      color: '#64748b',
                      ml: 0.5,
                    },
                  }}
                />
              ) : null}
            </Stack>

            <Tooltip title={title} arrow placement="top-start">
              <Typography
                sx={{
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: '0.9rem',
                  WebkitLineClamp: 2,
                  '@media (min-width: 1020px)': {
                    fontSize: '1rem',
                  },
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  textAlign: 'left',
                  WebkitLineClamp: { xs: 2, md: 2 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title}
              </Typography>
            </Tooltip>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                mt: { xs: 0.65, sm: 0.45 },
                color: '#64748b',
                fontSize: '0.72rem',
                fontWeight: 700,
                minWidth: 0,
              }}
            >
              <Tooltip title={locationLabel} arrow>
                <Typography
                  sx={{
                    color: '#334155',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {locationLabel}
                </Typography>
              </Tooltip>

              <Typography sx={{ color: '#cbd5e1' }}>•</Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.72rem',
                  fontWeight: 750,
                  whiteSpace: 'nowrap',
                }}
              >
                {this.formatDate(row.period_start)}
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 0.85 },
              bgcolor: '#f8fafc',
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              display: 'grid',
              gridTemplateColumns: '1.05fr 1fr 1fr',
              columnGap: 1.25,
              alignItems: 'center',

              '@media (min-width: 1020px)': {
                p: 0,
                bgcolor: 'transparent',
                borderTop: 0,
                display: 'contents',

                '& > div': {
                  px: 1.5,
                  py: 0,
                  minHeight: 104,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  borderLeft: '1px solid rgba(15, 23, 42, 0.06)',
                  bgcolor: isSummary
                    ? 'rgba(26, 35, 126, 0.025)'
                    : isNegative
                      ? 'rgba(239, 68, 68, 0.028)'
                      : 'rgba(34, 197, 94, 0.028)',
                },
              },
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: isNegative ? '#991b1b' : '#166534',
                  fontWeight: 950,
                  fontSize: { xs: '1.05rem', md: '1.35rem' },
                  lineHeight: 1,
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {this.formatPercent(row.difference_percent)}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: '#64748b',
                  fontWeight: 850,
                  fontSize: { xs: '0.62rem', md: '0.68rem' },
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                  whiteSpace: 'nowrap',
                }}
              >
                Vs expected
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontWeight: 800,
                  fontSize: { xs: '0.62rem', sm: '0.62rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                Actual
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: { xs: '0.86rem', md: '1.05rem' },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  alignItems: 'flex-start',
                  '@media (min-width: 1020px)': {
                    alignItems: 'center',
                  }
                }}
              >
                {this.formatMoney(row.actual_value)}
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontWeight: 800,
                  fontSize: { xs: '0.62rem', sm: '0.62rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                Expected
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: { xs: '0.86rem', md: '1.05rem' },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                {this.formatMoney(row.expected_value)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  }

  renderInsightDrawer() {
    const row = this.state.selectedInsight;

    if (!row) {
      return null;
    }

    const direction = this.getDirection(row);
    const severitySx = this.getSeverityColor(row.severity);
    const statusSx = this.getStatusColor(row.status);
    const status = Number(row.status);
    const isSummary = this.isDailySummary(row);
    const locationLabel = this.getLocationLabel(row);

    const actionButtonSx = {
      justifyContent: 'flex-start',
      borderRadius: 1.5,
      textTransform: 'none',
      fontWeight: 850,
      fontSize: '0.82rem',
      py: 0.85,
    };

    return (
      <Drawer
        anchor="right"
        open={this.state.drawerOpen}
        onClose={this.closeInsightDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            bgcolor: '#f8fafc',
          }
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              bgcolor: '#fff',
              borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 950,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                }}
              >
                Insight review
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#64748b',
                  fontWeight: 650,
                  fontSize: '0.76rem',
                }}
              >
                {locationLabel} • {this.formatDate(row.period_start)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={this.closeInsightDrawer}
              sx={{
                color: '#64748b',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              p: 1.25,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Stack
                direction="row"
                spacing={0.35}
                alignItems="center"
                sx={{
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={direction.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: direction.color,
                    bgcolor: direction.bg,
                    border: direction.border,
                  }}
                />

                <Chip
                  label={this.getSeverityLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    ...severitySx,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                  }}
                />

                <Chip
                  label={this.getStatusLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    ...statusSx,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  color: '#111827',
                  fontWeight: 950,
                  fontSize: '1rem',
                  lineHeight: 1.25,
                }}
              >
                {row.title || 'Insight'}
              </Typography>

              {row.summary ? (
                <Typography
                  sx={{
                    mt: 0.7,
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    lineHeight: 1.45,
                  }}
                >
                  {this.cleanText(row.summary)}
                </Typography>
              ) : null}
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Numbers
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 0.75,
                }}
              >
                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Actual
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.formatMoneyDetailed(row.actual_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Expected
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.formatMoneyDetailed(row.expected_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Difference
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.formatMoneyDetailed(row.difference_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Difference %
                  </Typography>
                  <Typography
                    sx={{
                      color: Number(row.difference_percent || 0) < 0 ? '#991b1b' : '#166534',
                      fontSize: '0.95rem',
                      fontWeight: 950,
                    }}
                  >
                    {this.formatPercent(row.difference_percent)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Context
              </Typography>

              <Stack spacing={0.65}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Type
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {this.getInsightTypeLabel(row)}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Location
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {locationLabel}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Period
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {this.formatDate(row.period_start)} - {this.formatDate(row.period_end)}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Baseline
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {row.baseline_type || '-'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Confidence
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {row.confidence !== null && row.confidence !== undefined ? row.confidence : '-'}
                  </Typography>
                </Stack>

                {isSummary && row.source_data ? (
                  <React.Fragment>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                        Locations included
                      </Typography>
                      <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                        {row.source_data.locations_included !== undefined ? row.source_data.locations_included : '-'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                        Locations skipped
                      </Typography>
                      <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                        {row.source_data.locations_skipped !== undefined ? row.source_data.locations_skipped : '-'}
                      </Typography>
                    </Stack>
                  </React.Fragment>
                ) : null}
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Review note
              </Typography>

              <TextField
                value={this.state.reviewNote}
                onChange={this.handleReviewNoteChange}
                fullWidth
                multiline
                minRows={3}
                size="small"
                placeholder="Example: Location closed, relocation, stock issue, known event, data issue..."
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                  },
                }}
              />
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Review actions
              </Typography>

              <Stack spacing={0.65}>
                {status === 0 ? (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() => this.setInsightStatus(1)}
                    sx={actionButtonSx}
                  >
                    Mark as Seen
                  </Button>
                ) : null}

                {[0, 1, 3, 4, 9].includes(status) ? (
                  <Button
                    variant="outlined"
                    startIcon={<RateReviewOutlinedIcon />}
                    onClick={() => this.setInsightStatus(2)}
                    sx={actionButtonSx}
                  >
                    Move to Review
                  </Button>
                ) : null}

                {[0, 1, 2].includes(status) ? (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => this.setInsightStatus(3)}
                    sx={actionButtonSx}
                  >
                    Resolve
                  </Button>
                ) : null}

                {[0, 1, 2].includes(status) ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={() => this.setInsightStatus(4)}
                    sx={actionButtonSx}
                  >
                    Ignore
                  </Button>
                ) : null}

                {[2, 3, 4].includes(status) ? (
                  <Button
                    variant="outlined"
                    startIcon={<ArchiveOutlinedIcon />}
                    onClick={() => this.setInsightStatus(9)}
                    sx={actionButtonSx}
                  >
                    Archive
                  </Button>
                ) : null}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Drawer>
    );
  }

  render() {
    const page = this.state.page;
    const rowsPerPage = this.state.rowsPerPage;
    const sortedInsightsList = [...(this.state.insights || [])].sort((a, b) => {
      const dateDiff = new Date(b.period_start || 0) - new Date(a.period_start || 0);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      const summaryDiff = Number(this.isDailySummary(b)) - Number(this.isDailySummary(a));
      if (summaryDiff !== 0) {
        return summaryDiff;
      }
      const severityDiff = Number(b.severity || 0) - Number(a.severity || 0);
      if (severityDiff !== 0) {
        return severityDiff;
      }
      return Math.abs(Number(b.difference_percent || 0)) - Math.abs(Number(a.difference_percent || 0));
    });

    const insightsList = sortedInsightsList.filter((row) => {
      const status = Number(row.status);

      if (this.state.statusView === "open") {
        return [0, 1, 2].includes(status);
      }
      if (this.state.statusView === "resolved") {
        return status === 3;
      }
      if (this.state.statusView === "ignored") {
        return status === 4;
      }
      if (this.state.statusView === "archived") {
        return status === 9;
      }

      return true;
    });

    const criticalHighCount = insightsList.filter((row) => Number(row.severity) >= 3).length;
    const openCount = sortedInsightsList.filter((row) => [0, 1, 2].includes(Number(row.status))).length;
    const summaryCount = insightsList.filter((row) => this.isDailySummary(row)).length;
    const attentionCount = insightsList.filter((row) => !this.isDailySummary(row) && Number(row.difference_percent || 0) < 0).length;
    const opportunityCount = insightsList.filter((row) => !this.isDailySummary(row) && Number(row.difference_percent || 0) > 0).length;

    const visibleRows = rowsPerPage > 0
      ? insightsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : insightsList;

    const controlSx = {
      minWidth: 0,
      '& .MuiInputBase-root': {
        height: 38,
        fontSize: { xs: 12.5, sm: 13.5 },
        bgcolor: '#fff',
        borderRadius: 1.5,
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: 12.5, sm: 13.5 },
      },
    };

    const paginationSx = {
      flexShrink: 0,
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      minHeight: { xs: 44, sm: 50 },
      '& .MuiTablePagination-toolbar': {
        minHeight: { xs: 44, sm: 50 },
        px: { xs: 0.5, sm: 1 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 0.25, sm: 0.5 },
      },
      '& .MuiTablePagination-spacer': {
        display: { xs: 'none', sm: 'block' },
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: { xs: '0.76rem', sm: '0.86rem' },
        m: 0,
      },
      '& .MuiTablePagination-actions': {
        ml: { xs: 0, sm: 1 },
      },
    };

    const isSingleDay = this.state.dateFrom && this.state.dateFrom === this.state.dateTo;
    const periodText = isSingleDay
      ? `Insights for ${this.formatDate(this.state.dateFrom)}`
      : `Insights from ${this.formatDate(this.state.dateFrom)} to ${this.formatDate(this.state.dateTo)}`;

    return (
      <Paper
        sx={{
          width: '100%',
          height: { xs: 650, sm: '100%' },
          maxHeight: { xs: 650, sm: '100%' },
          minHeight: 0,
          borderRadius: { xs: '14px', sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: 0,
          bgcolor: '#fff',
          containerType: 'inline-size',
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.25 },
            py: { xs: 0.9, sm: 1 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#1a237e',
                fontWeight: 950,
                fontSize: { xs: '1rem', sm: '1.05rem' },
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Intelligence inbox
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                display: { xs: 'none', sm: 'block' },
                color: '#64748b',
                fontWeight: 650,
                fontSize: '0.76rem',
                lineHeight: 1.25,
              }}
            >
              {periodText}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexShrink: 0, flexWrap: 'wrap' }}
          >
            <Chip
              label={`${insightsList.length} total`}
              size="small"
              sx={{
                height: 24,
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.08)',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />

            <Chip
              label={`${criticalHighCount} critical/high`}
              size="small"
              sx={{
                height: 24,
                color: '#991b1b',
                bgcolor: 'rgba(239, 68, 68, 0.10)',
                border: '1px solid rgba(239, 68, 68, 0.18)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />
            <Chip
              label={`${openCount} open`}
              size="small"
              sx={{
                height: 24,
                color: '#075985',
                bgcolor: 'rgba(14, 165, 233, 0.10)',
                border: '1px solid rgba(14, 165, 233, 0.18)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />

          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            mt: 0.75,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label="Open"
            size="small"
            onClick={() => this.handleStatusViewChange("open")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "open" ? '#1a237e' : '#334155',
              bgcolor: this.state.statusView === "open"
                ? 'rgba(26, 35, 126, 0.12)'
                : '#f8fafc',
              border: this.state.statusView === "open"
                ? '1px solid rgba(26, 35, 126, 0.20)'
                : '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: 'rgba(26, 35, 126, 0.10)',
              },
            }}
          />

          <Chip
            label="Resolved"
            size="small"
            onClick={() => this.handleStatusViewChange("resolved")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "resolved" ? '#166534' : '#334155',
              bgcolor: this.state.statusView === "resolved"
                ? 'rgba(34, 197, 94, 0.12)'
                : '#f8fafc',
              border: this.state.statusView === "resolved"
                ? '1px solid rgba(34, 197, 94, 0.22)'
                : '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: 'rgba(34, 197, 94, 0.08)',
              },
            }}
          />

          <Chip
            label="Ignored"
            size="small"
            onClick={() => this.handleStatusViewChange("ignored")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "ignored" ? '#475569' : '#334155',
              bgcolor: this.state.statusView === "ignored"
                ? 'rgba(100, 116, 139, 0.14)'
                : '#f8fafc',
              border: '1px solid rgba(100, 116, 139, 0.16)',
              '&:hover': {
                bgcolor: 'rgba(100, 116, 139, 0.10)',
              },
            }}
          />

          <Chip
            label="Archived"
            size="small"
            onClick={() => this.handleStatusViewChange("archived")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "archived" ? '#475569' : '#334155',
              bgcolor: this.state.statusView === "archived"
                ? 'rgba(100, 116, 139, 0.14)'
                : '#f8fafc',
              border: '1px solid rgba(100, 116, 139, 0.16)',
              '&:hover': {
                bgcolor: 'rgba(100, 116, 139, 0.10)',
              },
            }}
          />

          <Chip
            label="All"
            size="small"
            onClick={() => this.handleStatusViewChange("all")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "all" ? '#1a237e' : '#334155',
              bgcolor: this.state.statusView === "all"
                ? 'rgba(26, 35, 126, 0.10)'
                : '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: '#eef2ff',
              },
            }}
          />

          {summaryCount > 0 ? (
            <Chip
              label={`${summaryCount} summary`}
              size="small"
              sx={{
                height: 24,
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.06)',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            px: 0.75,
            py: 0.6,
            display: 'flex',
            '@container (min-width: 900px)': {
              display: 'none',
            },
            alignItems: 'center',
            flexWrap: 'wrap',
            borderBottom: this.state.showMobileFilters
              ? 0
              : '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
          }}
        >
          <Chip
            label={this.state.showMobileFilters ? 'Hide filters' : 'Filters'}
            size="small"
            icon={this.state.showMobileFilters ? <ExpandLessIcon /> : <TuneIcon />}
            onClick={() =>
              this.setState((prev) => ({
                showMobileFilters: !prev.showMobileFilters,
              }))
            }
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: '#1a237e',
              bgcolor: this.state.showMobileFilters
                ? 'rgba(26, 35, 126, 0.12)'
                : 'rgba(26, 35, 126, 0.06)',
              border: '1px solid rgba(26, 35, 126, 0.18)',
              '& .MuiChip-icon': {
                color: '#1a237e',
                fontSize: '0.95rem',
              },
            }}
          />

          <Chip
            label={this.state.dateFrom === this.state.dateTo
              ? this.formatDate(this.state.dateFrom)
              : `${this.formatDate(this.state.dateFrom)} - ${this.formatDate(this.state.dateTo)}`
            }
            size="small"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: '#334155',
              bgcolor: '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.10)',
            }}
          />

          {this.state.severity !== "" ? (
            <Chip
              label={`Severity ${this.state.severity}`}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
              }}
            />
          ) : null}

          {this.state.insightType !== "" ? (
            <Chip
              label={`Type ${this.state.insightType}`}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
              }}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.75, sm: 0.85 },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
            display: this.state.showMobileFilters ? 'block' : 'none',
            '@container (min-width: 900px)': {
              display: 'block',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 0.55, sm: 0.65 },
              alignItems: 'center',
              '& > .MuiTextField-root': {
                flex: '0 0 auto',
                minWidth: 0,
              },
              '& > .MuiTextField-root:nth-of-type(1), & > .MuiTextField-root:nth-of-type(2)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 130, lg: 140 },
              },
              '& > .MuiTextField-root:nth-of-type(3)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 110, lg: 115 },
              },
              '& > .MuiTextField-root:nth-of-type(4)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 120, lg: 125 },
              },
              '& > .MuiTextField-root:nth-of-type(5)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 110, lg: 115 },
              },
            }}
          >
            <TextField
              size="small"
              type="date"
              label="From"
              name="dateFrom"
              value={this.state.dateFrom}
              onChange={this.handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...controlSx,
                '& input': {
                  px: { xs: 0.55, sm: 0.75 },
                  fontSize: { xs: 13.5, sm: 14 },
                  letterSpacing: { xs: -0.4, sm: -0.25 },
                },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              name="dateTo"
              value={this.state.dateTo}
              onChange={this.handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...controlSx,
                '& input': {
                  px: { xs: 0.55, sm: 0.75 },
                  fontSize: { xs: 13.5, sm: 14 },
                  letterSpacing: { xs: -0.4, sm: -0.25 },
                },
              }}
            />

            <TextField
              size="small"
              select
              label="Severity"
              name="severity"
              value={this.state.severity}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Severity</MenuItem>
              <MenuItem value="1">Low</MenuItem>
              <MenuItem value="2">Medium</MenuItem>
              <MenuItem value="3">High</MenuItem>
              <MenuItem value="4">Critical</MenuItem>
            </TextField>

            <TextField
              size="small"
              select
              label="Status"
              name="status"
              value={this.state.status}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Status</MenuItem>
              <MenuItem value="0">New</MenuItem>
              <MenuItem value="1">Seen</MenuItem>
              <MenuItem value="2">In Review</MenuItem>
              <MenuItem value="3">Resolved</MenuItem>
              <MenuItem value="4">Ignored</MenuItem>
              <MenuItem value="9">Archived</MenuItem>
            </TextField>

            <TextField
              size="small"
              select
              label="Type"
              name="insightType"
              value={this.state.insightType}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Type</MenuItem>
              <MenuItem value="1001">Below</MenuItem>
              <MenuItem value="1002">Above</MenuItem>
              <MenuItem value="1003">Summary</MenuItem>
            </TextField>

            <Tooltip title="Reset filters" arrow>
              <IconButton
                size="small"
                onClick={this.clearFilters}
                sx={{
                  width: { xs: 'calc((100% - 8px) / 2)', sm: 38 },
                  flex: '0 0 auto',
                  height: { xs: 38, sm: 38 },
                  borderRadius: 1.5,
                  color: '#1a237e',
                  border: '1px solid rgba(26, 35, 126, 0.24)',
                  bgcolor: '#fff',
                  justifySelf: 'stretch',
                  '&:hover': {
                    borderColor: 'rgba(26, 35, 126, 0.42)',
                    bgcolor: 'rgba(26, 35, 126, 0.04)',
                  },
                }}
              >
                <RestartAltIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mt: 0.75,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label="Yesterday"
              size="small"
              onClick={() => this.applyDatePreset("yesterday")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#1a237e',
                bgcolor: this.state.dateFrom === getLocalDateString(addDays(new Date(), -1)) &&
                  this.state.dateTo === getLocalDateString(addDays(new Date(), -1))
                  ? 'rgba(26, 35, 126, 0.12)'
                  : 'rgba(26, 35, 126, 0.05)',
                border: '1px solid rgba(26, 35, 126, 0.16)',
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.10)',
                },
              }}
            />

            <Chip
              label="Today"
              size="small"
              onClick={() => this.applyDatePreset("today")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
                '&:hover': {
                  bgcolor: '#eef2ff',
                },
              }}
            />

            <Chip
              label="Last 7 days"
              size="small"
              onClick={() => this.applyDatePreset("last7")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
                '&:hover': {
                  bgcolor: '#eef2ff',
                },
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mt: 0.75,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 750 }}>
              Attention: {attentionCount}
            </Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.72rem' }}>•</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 750 }}>
              Opportunity: {opportunityCount}
            </Typography>
          </Stack>
        </Box>

        <Box
          ref={this.listRef}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            p: { xs: 0.75, sm: 1 },
            bgcolor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.6, md: 0.7 },
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(15, 23, 42, 0.18)',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
            },
          }}
        >
          {visibleRows.map((row) => this.renderInsightCard(row))}

          {insightsList.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: '14px',
                textAlign: 'center',
                color: 'text.secondary',
                borderColor: 'rgba(15, 23, 42, 0.08)',
                bgcolor: '#fff',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#334155' }}>
                No insights found
              </Typography>

              <Typography sx={{ mt: 0.4, fontWeight: 500, fontSize: '0.8rem', color: '#64748b' }}>
                There are no insights or summaries matching the selected filters.
              </Typography>
            </Paper>
          ) : null}
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 8, 12, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={insightsList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={paginationSx}
        />

        {this.renderInsightDrawer()}

        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={3000}
          onClose={this.closeSnackbar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={this.closeSnackbar}
            severity={this.state.snackbarSeverity}
            variant="filled"
            sx={{
              width: '100%',
              fontWeight: 800,
            }}
          >
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    );
  }
}

export default IntelligenceInsights;